import React, { useEffect, useMemo, useRef, useState } from 'react';
import { ActivityIndicator, Platform, Pressable, StyleSheet, Text, View } from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { useLocalSearchParams, useRouter } from 'expo-router';
import {
  completePaymentOrder,
  completePublicPaymentOrder,
  createPaymentCaptureContext,
  createPublicPaymentCaptureContext,
  createPublicPaymentOrder,
} from '../src/api/client';
import type { PaymentOrder } from '../src/types/api';
import { useAuthStore } from '../src/stores/authStore';
import { useTheme } from '../src/utils/theme';
import { FONT_SIZE, RADIUS, SPACING } from '../src/utils/design-tokens';

const CHECKOUT_CONTAINER_ID = 'cybersource-unified-checkout';
const CHECKOUT_ANCHOR_ID = 'cybersource-unified-checkout-anchor';
const CHECKOUT_PARAM_STORAGE_KEY = 'fm-checkout-params';
const CHECKOUT_PARAM_TTL_MS = 30 * 60 * 1000;
const checkoutSessionPromises = new Map<string, Promise<{ order: PaymentOrder; captureContext: any }>>();

export default function CheckoutScreen() {
  const router = useRouter();
  const params = useLocalSearchParams<{ token?: string | string[]; orderId?: string | string[] }>();
  const tokenParam = readParam(params.token);
  const orderIdSearchParam = readParam(params.orderId);

  // Web: hand off to standalone /pay page (vanilla JS, outside React Native Web).
  // Native: keep the legacy in-app flow below.
  useEffect(() => {
    if (Platform.OS !== 'web' || typeof window === 'undefined') return;
    const hash = readHashCheckoutParams();
    const stored = readSavedCheckoutParams();
    const orderId = orderIdSearchParam || hash.orderId || stored.orderId;
    const token = tokenParam || hash.token || stored.token;
    const qs = new URLSearchParams();
    if (orderId) qs.set('orderId', orderId);
    if (token) qs.set('token', token);
    clearSavedCheckoutParams();
    window.location.replace(`/pay${qs.toString() ? `?${qs.toString()}` : ''}`);
  }, [tokenParam, orderIdSearchParam]);

  const savedParams = useMemo(() => readSavedCheckoutParams(), []);
  const [routeParams, setRouteParams] = useState({ token: '', orderId: '', ready: Platform.OS !== 'web' });
  const token = tokenParam || routeParams.token || savedParams.token;
  const orderIdParam = orderIdSearchParam || routeParams.orderId || savedParams.orderId;
  const updateUser = useAuthStore((state) => state.updateUser);
  const { colors } = useTheme();
  const styles = useMemo(() => createStyles(colors), [colors]);
  const [order, setOrder] = useState<PaymentOrder | null>(null);
  const [status, setStatus] = useState<'loading' | 'ready' | 'processing' | 'completed' | 'error'>('loading');
  const [message, setMessage] = useState('Preparing checkout');
  const [autoRenewConsent, setAutoRenewConsent] = useState(false);
  const didStart = useRef(false);
  const didComplete = useRef(false);
  const cleanupCheckoutRef = useRef<(() => void) | null>(null);
  // The CyberSource onToken callback runs outside React's render cycle, so it
  // reads the consent flag from a ref kept in sync with the checkbox state.
  const autoRenewConsentRef = useRef(false);
  autoRenewConsentRef.current = autoRenewConsent;

  useEffect(() => {
    let cancelled = false;
    let attempts = 0;

    const resolveRouteParams = () => {
      const hashParams = readHashCheckoutParams();
      const storedParams = readSavedCheckoutParams();
      const routeToken = tokenParam || hashParams.token || storedParams.token;
      const routeOrderId = orderIdSearchParam || hashParams.orderId || storedParams.orderId;
      if (Platform.OS === 'web' && !routeToken && !routeOrderId && attempts < 30) {
        attempts += 1;
        window.requestAnimationFrame(resolveRouteParams);
        return;
      }
      if (routeToken || routeOrderId) {
        saveCheckoutParams({ token: routeToken, orderId: routeOrderId });
      }
      if (Platform.OS === 'web' && (tokenParam || orderIdSearchParam)) {
        replaceCheckoutHash({ token: tokenParam, orderId: orderIdSearchParam });
      }
      if (!cancelled) setRouteParams({ token: routeToken, orderId: routeOrderId, ready: true });
    };

    resolveRouteParams();
    return () => { cancelled = true; };
  }, [tokenParam, orderIdSearchParam]);

  useEffect(() => {
    if (!routeParams.ready) return;
    if (didStart.current) return;
    didStart.current = true;
    startCheckout().catch((error) => {
      setStatus('error');
      setMessage(error?.message || 'Checkout could not be started');
    });
  }, [routeParams.ready, token, orderIdParam]);

  useEffect(() => () => {
    cleanupCheckoutRef.current?.();
    cleanupCheckoutRef.current = null;
  }, []);

  const renewalPrice = order ? `$${Number(order.amountUsd).toFixed(2)}` : 'the plan price';
  const renewalMonths = order?.durationMonths || 0;
  const renewalDisclosure =
    `Save my card and automatically renew my subscription. You'll be charged ${renewalPrice} ` +
    `every ${renewalMonths || 'N'} month${renewalMonths === 1 ? '' : 's'} until you cancel. ` +
    `Cancel anytime in Settings.`;

  const startCheckout = async () => {
    if (Platform.OS !== 'web') {
      throw new Error('Card checkout is available in the web app.');
    }
    if (!token && !orderIdParam) {
      throw new Error('Checkout link is missing a payment order.');
    }

    setStatus('loading');
    setMessage('Creating secure payment session');

    const checkoutKey = getCheckoutSessionKey({ token, orderId: orderIdParam });
    const { order: currentOrder, captureContext } = await getCheckoutSession({ token, orderId: orderIdParam });
    setOrder(currentOrder);

    if (!captureContext.clientLibraryUrl) {
      throw new Error('CyberSource did not return a checkout client library URL.');
    }

    setStatus('ready');
    setMessage('Loading secure card form');
    await loadScript(captureContext.clientLibraryUrl);
    await waitForUnifiedCheckoutSdk();

    await mountUnifiedCheckout({
      sessionJwt: captureContext.sessionJwt,
      containerId: CHECKOUT_CONTAINER_ID,
      anchorId: CHECKOUT_ANCHOR_ID,
      onCleanupReady: (cleanup) => {
        cleanupCheckoutRef.current = cleanup;
      },
      onReady: () => {
        setStatus('ready');
        setMessage('Enter payment details');
      },
      onToken: async (transientTokenJwt) => {
        if (didComplete.current) return;
        didComplete.current = true;
        setStatus('processing');
        setMessage('Finalizing payment');
        const enableAutoRenew = autoRenewConsentRef.current;
        const result = token
          ? await completePublicPaymentOrder(currentOrder.id, token, transientTokenJwt, enableAutoRenew)
          : await completePaymentOrder(currentOrder.id, transientTokenJwt, enableAutoRenew);
        if (result.user) updateUser(result.user);
        clearSavedCheckoutParams();
        clearCheckoutSession(checkoutKey);
        cleanupCheckoutRef.current?.();
        cleanupCheckoutRef.current = null;
        setOrder(result.order);
        setStatus('completed');
        setMessage('Subscription activated');
      },
      onError: (error) => {
        if (didComplete.current) return;
        setStatus('error');
        setMessage(error?.message || 'Payment form failed to load');
      },
    });
  };

  return (
    <View style={styles.screen}>
      <View style={styles.header}>
        <Pressable style={styles.iconButton} onPress={() => router.back()} accessibilityRole="button" accessibilityLabel="Back">
          <Ionicons name="chevron-back" size={22} color={colors.text} />
        </Pressable>
        <Text style={styles.title}>Checkout</Text>
        <View style={styles.iconButton} />
      </View>

      <View style={styles.summary}>
        <View style={{ flex: 1 }}>
          <Text style={styles.plan}>{order?.bundleLabel || 'FishMemory Premium'}</Text>
          <Text style={styles.meta}>
            {order ? `$${Number(order.amountUsd).toFixed(2)} ${order.currency} / ${order.durationMonths}m` : 'Secure payment'}
          </Text>
        </View>
        {status === 'completed' ? (
          <Ionicons name="checkmark-circle" size={24} color={colors.success} />
        ) : status === 'error' ? (
          <Ionicons name="alert-circle" size={24} color={colors.error} />
        ) : (
          <Ionicons name="card-outline" size={24} color={colors.primary} />
        )}
      </View>

      {status !== 'completed' && (
        <Pressable
          style={styles.consentRow}
          onPress={() => setAutoRenewConsent((value) => !value)}
          accessibilityRole="checkbox"
          accessibilityState={{ checked: autoRenewConsent }}
          accessibilityLabel="Enable subscription auto-renewal"
        >
          <View style={[styles.checkbox, autoRenewConsent && styles.checkboxChecked]}>
            {autoRenewConsent ? <Ionicons name="checkmark" size={14} color="#fff" /> : null}
          </View>
          <Text style={styles.consentText}>{renewalDisclosure}</Text>
        </Pressable>
      )}

      <View nativeID={Platform.OS === 'web' ? CHECKOUT_ANCHOR_ID : CHECKOUT_CONTAINER_ID} style={styles.checkoutMount} />

      <View style={styles.statusRow}>
        {status === 'loading' || status === 'processing' ? <ActivityIndicator size="small" color={colors.primary} /> : null}
        <Text style={[styles.statusText, status === 'error' && { color: colors.error }, status === 'completed' && { color: colors.success }]}>
          {message}
        </Text>
      </View>

      {status === 'completed' && (
        <Pressable style={styles.primaryButton} onPress={() => router.replace('/(tabs)/settings')} accessibilityRole="button">
          <Text style={styles.primaryButtonText}>Done</Text>
        </Pressable>
      )}
    </View>
  );
}

function readParam(value?: string | string[]) {
  if (Array.isArray(value)) return value[0];
  return value || '';
}

function readHashCheckoutParams() {
  if (typeof window === 'undefined') return { token: '', orderId: '' };
  try {
    const hash = window.location.hash.replace(/^#/, '');
    const params = new URLSearchParams(hash);
    return {
      token: params.get('token') || '',
      orderId: params.get('orderId') || '',
    };
  } catch {
    return { token: '', orderId: '' };
  }
}

function replaceCheckoutHash({ token, orderId }: { token: string; orderId: string }) {
  if (typeof window === 'undefined' || (!token && !orderId)) return;
  try {
    const hashParams = new URLSearchParams();
    if (token) hashParams.set('token', token);
    if (!token && orderId) hashParams.set('orderId', orderId);
    const nextUrl = `${window.location.pathname}#${hashParams.toString()}`;
    if (`${window.location.pathname}${window.location.hash}` !== nextUrl) {
      window.history.replaceState(window.history.state, '', nextUrl);
    }
  } catch { /* ignore history normalization errors */ }
}

function readSavedCheckoutParams() {
  if (typeof window === 'undefined') return { token: '', orderId: '' };
  try {
    const raw = window.sessionStorage.getItem(CHECKOUT_PARAM_STORAGE_KEY);
    if (!raw) return { token: '', orderId: '' };
    const parsed = JSON.parse(raw);
    if (!parsed?.savedAt || Date.now() - Number(parsed.savedAt) > CHECKOUT_PARAM_TTL_MS) {
      clearSavedCheckoutParams();
      return { token: '', orderId: '' };
    }
    return {
      token: typeof parsed.token === 'string' ? parsed.token : '',
      orderId: typeof parsed.orderId === 'string' ? parsed.orderId : '',
    };
  } catch {
    return { token: '', orderId: '' };
  }
}

function saveCheckoutParams({ token, orderId }: { token: string; orderId: string }) {
  if (typeof window === 'undefined' || (!token && !orderId)) return;
  try {
    const current = JSON.parse(window.sessionStorage.getItem(CHECKOUT_PARAM_STORAGE_KEY) || 'null');
    if (current?.token === token && current?.orderId === orderId && current?.savedAt && Date.now() - Number(current.savedAt) < CHECKOUT_PARAM_TTL_MS) {
      return;
    }
    window.sessionStorage.setItem(CHECKOUT_PARAM_STORAGE_KEY, JSON.stringify({ token, orderId, savedAt: Date.now() }));
  } catch { /* ignore session storage errors */ }
}

function clearSavedCheckoutParams() {
  if (typeof window === 'undefined') return;
  try { window.sessionStorage.removeItem(CHECKOUT_PARAM_STORAGE_KEY); } catch { /* ignore session storage errors */ }
}

function getCheckoutSessionKey({ token, orderId }: { token: string; orderId: string }) {
  return token ? `token:${token}` : `order:${orderId}`;
}

function clearCheckoutSession(key: string) {
  checkoutSessionPromises.delete(key);
}

async function getCheckoutSession({ token, orderId }: { token: string; orderId: string }) {
  const key = getCheckoutSessionKey({ token, orderId });
  if (!checkoutSessionPromises.has(key)) {
    checkoutSessionPromises.set(key, createCheckoutSession({ token, orderId }));
  }
  return checkoutSessionPromises.get(key)!;
}

async function createCheckoutSession({ token, orderId }: { token: string; orderId: string }) {
  if (token) {
    const orderResponse = await createPublicPaymentOrder(token);
    const captureContext = await createPublicPaymentCaptureContext(orderResponse.order.id, token);
    return { order: orderResponse.order, captureContext };
  }
  const captureContext = await createPaymentCaptureContext(orderId);
  return { order: captureContext.order, captureContext };
}

function loadScript(src: string) {
  return new Promise<void>((resolve, reject) => {
    if (typeof document === 'undefined') return reject(new Error('Browser document is unavailable'));
    const existing = document.querySelector<HTMLScriptElement>(`script[src="${src}"]`);
    if (existing) {
      if (existing.dataset.fmLoaded === 'true' || getUnifiedCheckoutRoots(window as any).length > 0) return resolve();
      existing.addEventListener('load', () => resolve(), { once: true });
      existing.addEventListener('error', () => reject(new Error('Could not load CyberSource checkout library')), { once: true });
      return;
    }
    const script = document.createElement('script');
    script.src = src;
    script.async = true;
    script.onload = () => {
      script.dataset.fmLoaded = 'true';
      resolve();
    };
    script.onerror = () => reject(new Error('Could not load CyberSource checkout library'));
    document.head.appendChild(script);
  });
}

function getUnifiedCheckoutRoots(checkoutWindow: any) {
  return [checkoutWindow.VAS, checkoutWindow.VisaAcceptance, checkoutWindow.CyberSource, checkoutWindow.Accept, checkoutWindow.Flex]
    .filter((root) => root?.UnifiedCheckout || root?.unifiedCheckout || root?.createUnifiedCheckout);
}

function waitForUnifiedCheckoutSdk(timeoutMs = 8000) {
  const startedAt = Date.now();
  return new Promise<void>((resolve, reject) => {
    const check = () => {
      if (typeof window !== 'undefined' && getUnifiedCheckoutRoots(window as any).length > 0) return resolve();
      if (Date.now() - startedAt > timeoutMs) return reject(new Error('CyberSource Unified Checkout API was not found on the loaded library'));
      window.setTimeout(check, 100);
    };
    check();
  });
}

function prepareHostedCheckoutContainer(containerId: string, anchorId: string) {
  const anchor = document.getElementById(anchorId);
  if (!anchor) throw new Error('Checkout container was not found');

  const existing = document.getElementById(containerId);
  if (existing?.dataset.fmHostedCheckout === 'true') {
    (existing as any).__fmPositionCleanup?.();
    existing.remove();
  }

  const container = document.createElement('div');
  container.id = containerId;
  container.dataset.fmHostedCheckout = 'true';
  document.body.appendChild(container);
  (container as any).__fmPositionCleanup = positionHostedCheckoutContainer(container, anchor);
  return container;
}

function positionHostedCheckoutContainer(container: HTMLElement, anchor: HTMLElement) {
  const apply = () => {
    const rect = anchor.getBoundingClientRect();
    const height = Math.max(rect.height || 0, 420);
    Object.assign(container.style, {
      position: 'fixed',
      left: `${rect.left}px`,
      top: `${rect.top}px`,
      width: `${rect.width}px`,
      height: `${height}px`,
      minHeight: `${height}px`,
      background: '#fff',
      borderRadius: '8px',
      boxSizing: 'border-box',
      overflow: 'visible',
      zIndex: '20',
    });
  };
  apply();
  window.addEventListener('resize', apply);
  const resizeObserver = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(apply) : null;
  resizeObserver?.observe(anchor);
  const interval = window.setInterval(apply, 250);
  return () => {
    window.removeEventListener('resize', apply);
    resizeObserver?.disconnect();
    window.clearInterval(interval);
  };
}

async function mountUnifiedCheckout({ sessionJwt, containerId, anchorId, onCleanupReady, onReady, onToken, onError }: {
  sessionJwt: string;
  containerId: string;
  anchorId: string;
  onCleanupReady: (cleanup: () => void) => void;
  onReady: () => void;
  onToken: (transientTokenJwt: string) => Promise<void>;
  onError: (error: Error) => void;
}) {
  const checkoutWindow = window as any;
  try { checkoutWindow.__fishmemoryCheckoutCleanup?.(); } catch { /* ignore previous checkout cleanup errors */ }
  const container = prepareHostedCheckoutContainer(containerId, anchorId);
  clearElement(container);

  const roots = getUnifiedCheckoutRoots(checkoutWindow);
  let lastError: Error | null = null;
  for (const root of roots) {
    const UnifiedCheckout = root.UnifiedCheckout || root.unifiedCheckout || root.createUnifiedCheckout;
    if (!UnifiedCheckout) continue;
    let cleanup: (() => void) | null = null;
    try {
      const client = await createCheckoutInstance(UnifiedCheckout, sessionJwt);
      attachCheckoutEvents(client, onToken, onError);

      if (typeof client?.createTrigger === 'function') {
        const trigger = client.createTrigger('PANENTRY', { autoProcessing: false });
        attachCheckoutEvents(trigger, onToken, onError);
        cleanup = registerCheckoutCleanup({ objects: [trigger, client], container, checkoutWindow, onCleanupReady });
        const mountPromise = trigger.mount(`#${container.id}`);
        await waitForVisiblePaymentFrame(container);
        onReady();
        const maybeToken = await mountPromise;
        const token = extractTransientToken(maybeToken);
        if (token) await onToken(token);
        return;
      }

      const instance = typeof client?.createCheckout === 'function'
        ? await client.createCheckout({ autoProcessing: false })
        : client;
      attachCheckoutEvents(instance, onToken, onError);
      cleanup = registerCheckoutCleanup({ objects: client === instance ? [instance] : [instance, client], container, checkoutWindow, onCleanupReady });
      const maybeToken = await renderCheckoutInstance(instance, container, sessionJwt);
      onReady();
      if (maybeToken) await onToken(maybeToken);
      return;
    } catch (error: any) {
      lastError = error instanceof Error ? error : new Error(error?.message || 'Payment form failed to load');
      try { cleanup?.(); } catch { /* ignore failed checkout cleanup */ }
      clearElement(container);
    }
  }
  throw lastError || new Error('CyberSource Unified Checkout API was not found on the loaded library');
}

function registerCheckoutCleanup({ objects, container, checkoutWindow, onCleanupReady }: {
  objects: any[];
  container: HTMLElement;
  checkoutWindow: any;
  onCleanupReady: (cleanup: () => void) => void;
}) {
  const cleanup = () => {
    for (const object of objects) {
      try { object?.destroy?.(); } catch { /* ignore CyberSource cleanup errors */ }
      try { object?.unmount?.(); } catch { /* ignore CyberSource cleanup errors */ }
    }
    try { (container as any).__fmPositionCleanup?.(); } catch { /* ignore checkout positioning cleanup */ }
    clearElement(container);
    if (container.dataset.fmHostedCheckout === 'true') container.remove();
    removeCyberSourceHostedArtifacts();
    if (checkoutWindow.__fishmemoryCheckoutCleanup === cleanup) {
      checkoutWindow.__fishmemoryCheckoutCleanup = null;
    }
  };
  checkoutWindow.__fishmemoryCheckoutCleanup = cleanup;
  onCleanupReady(cleanup);
  return cleanup;
}

function waitForVisiblePaymentFrame(container: HTMLElement, timeoutMs = 8000) {
  const startedAt = Date.now();
  return new Promise<void>((resolve, reject) => {
    const check = () => {
      const frames = Array.from(container.querySelectorAll('iframe'));
      const paymentFrame = frames.find((item) => item.id === '__mce') || frames[0];
      if (paymentFrame && paymentFrame.getBoundingClientRect().height <= 80) {
        paymentFrame.style.height = '391px';
        paymentFrame.style.minHeight = '391px';
        paymentFrame.style.width = '100%';
      }
      const frame = frames.find((item) => item.getBoundingClientRect().height > 80);
      if (frame) return resolve();
      if (Date.now() - startedAt > timeoutMs) return reject(new Error('CyberSource payment form did not become visible'));
      window.setTimeout(check, 100);
    };
    check();
  });
}

function removeCyberSourceHostedArtifacts() {
  const ids = new Set(['__mce', '__ga', '__tm', '__orc', '__buttonlist', '__ctp', '__check', '__stepup', '__tms']);
  for (const frame of Array.from(document.querySelectorAll('iframe'))) {
    if (ids.has(frame.id)) frame.remove();
  }
}

async function createCheckoutInstance(factory: any, sessionJwt: string) {
  try {
    return await factory(sessionJwt);
  } catch {
    return new factory(sessionJwt);
  }
}

function attachCheckoutEvents(instance: any, onToken: (token: string) => Promise<void>, onError: (error: Error) => void) {
  if (!instance?.on) return;
  const tokenEvents = ['transientToken', 'paymentSuccess', 'payment:success', 'payment:authorized', 'complete', 'checkout:complete'];
  for (const eventName of tokenEvents) {
    subscribeCheckoutEvent(instance, eventName, async (payload: any) => {
      const token = extractTransientToken(payload);
      if (token) await onToken(token);
    });
  }
  const errorEvents = ['error', 'paymentError', 'payment:error', 'checkout:error'];
  for (const eventName of errorEvents) {
    subscribeCheckoutEvent(instance, eventName, (payload: any) => onError(new Error(payload?.message || 'CyberSource checkout error')));
  }
}

function subscribeCheckoutEvent(instance: any, eventName: string, handler: (payload: any) => void) {
  try {
    instance.on(eventName, handler);
  } catch { /* Unsupported event names vary by SDK surface. */ }
}

async function renderCheckoutInstance(instance: any, container: HTMLElement, sessionJwt: string) {
  const target = `#${container.id}`;
  const attempts = [
    { method: instance.mount, args: [target] },
    { method: instance.mount, args: [container] },
    { method: instance.render, args: [target] },
    { method: instance.render, args: [container] },
    { method: instance.show, args: [{ container: target, captureContext: sessionJwt }] },
    { method: instance.show, args: [target] },
    { method: instance.show, args: [] },
  ];
  for (const attempt of attempts) {
    if (typeof attempt.method !== 'function') continue;
    try {
      const result = await attempt.method.apply(instance, attempt.args);
      const token = extractTransientToken(result);
      if (token) return token;
      return null;
    } catch {
      clearElement(container);
    }
  }
  throw new Error('CyberSource checkout instance could not be rendered');
}

function clearElement(element: HTMLElement) {
  while (element.firstChild) element.removeChild(element.firstChild);
}

function extractTransientToken(payload: any): string | null {
  if (!payload) return null;
  if (typeof payload === 'string') return payload.includes('.') ? payload : null;
  return payload.transientTokenJwt
    || payload.transientToken
    || payload.jwt
    || payload.token
    || payload.paymentInformation?.transientTokenJwt
    || null;
}

function createStyles(colors: any) {
  return StyleSheet.create({
    screen: {
      flex: 1,
      backgroundColor: colors.background,
      paddingHorizontal: SPACING.base,
      paddingTop: SPACING.lg,
      gap: SPACING.base,
    },
    header: {
      flexDirection: 'row',
      alignItems: 'center',
      justifyContent: 'space-between',
    },
    iconButton: {
      width: 40,
      height: 40,
      borderRadius: RADIUS.md,
      alignItems: 'center',
      justifyContent: 'center',
    },
    title: {
      fontSize: FONT_SIZE.lg,
      fontFamily: 'Inter_600SemiBold',
      color: colors.text,
    },
    summary: {
      minHeight: 76,
      borderRadius: RADIUS.md,
      borderWidth: 1,
      borderColor: colors.border,
      backgroundColor: colors.surface,
      padding: SPACING.base,
      flexDirection: 'row',
      alignItems: 'center',
      gap: SPACING.md,
    },
    plan: {
      fontSize: FONT_SIZE.base,
      fontFamily: 'Inter_600SemiBold',
      color: colors.text,
    },
    meta: {
      marginTop: 3,
      fontSize: FONT_SIZE.sm,
      color: colors.textSecondary,
    },
    consentRow: {
      flexDirection: 'row',
      alignItems: 'flex-start',
      gap: SPACING.md,
      borderRadius: RADIUS.md,
      borderWidth: 1,
      borderColor: colors.border,
      backgroundColor: colors.surface,
      padding: SPACING.base,
    },
    checkbox: {
      width: 22,
      height: 22,
      borderRadius: RADIUS.sm,
      borderWidth: 1.5,
      borderColor: colors.border,
      alignItems: 'center',
      justifyContent: 'center',
      marginTop: 1,
    },
    checkboxChecked: {
      backgroundColor: colors.primary,
      borderColor: colors.primary,
    },
    consentText: {
      flex: 1,
      fontSize: FONT_SIZE.sm,
      color: colors.textSecondary,
      lineHeight: 19,
    },
    checkoutMount: {
      minHeight: 420,
      borderRadius: RADIUS.md,
      borderWidth: 1,
      borderColor: colors.border,
      backgroundColor: colors.surface,
      overflow: 'hidden',
    },
    statusRow: {
      minHeight: 32,
      flexDirection: 'row',
      alignItems: 'center',
      justifyContent: 'center',
      gap: SPACING.sm,
    },
    statusText: {
      fontSize: FONT_SIZE.sm,
      color: colors.textSecondary,
      textAlign: 'center',
    },
    primaryButton: {
      minHeight: 48,
      borderRadius: RADIUS.md,
      backgroundColor: colors.primary,
      alignItems: 'center',
      justifyContent: 'center',
    },
    primaryButtonText: {
      color: '#fff',
      fontSize: FONT_SIZE.base,
      fontFamily: 'Inter_600SemiBold',
    },
  });
}
